import React from 'react';
import styled, { keyframes } from 'styled-components';
import * as size from '@constant/size';
import * as color from '@constant/color';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { usePlaySound } from '@hook/common/useSound';
import TextBlock from '@component/common/block/text-block';
import { EStudyConfigurationMode, TClozeLevel, TIyagigoSubMenu } from '@datatype/iyagigo/type';
import { EApiClozeLevel } from '@datatype/iyagigo/api-type';
import { actions as modalActions } from '@store/slice/modal-slice';
import { actions as navigationActions, DEFAULT_IYAGIGO_SUB_MENU } from '@store/slice/navigation-slice';
import { actions as workbookActions } from '@store/slice/workbook-slice';
import { EWorkbookStudyType } from '@datatype/iyagigo/type';
import { useTodayClozeWorkbookQuery, useStudentKeyQuery } from '@hook/iyagigo/useApi';
import { getClozeGoal, getClozeTypeBySubject } from '@util/workbook';
import { useCurrentSubMenu } from '@hook/iyagigo/useSync';
import IconBlock from '@component/common/block/icon-block';
import { useWorkbookSync } from '@hook/iyagigo/useStudent';
import WorkbookTemplate from '@component/iyagigo/common/workbook-template';
import AssignmentControl from '@component/iyagigo/common/workbook-template/assignment-control';
import PieProgress from '@component/iyagigo/common/workbook-template/pie-progress';
import Blur from '@component/common/blur';
import Gap from '@component/common/gap';
import { useAuthPage } from '@hook/auth/useAuth';
import { useNavigate } from 'react-router-dom';
import { createClozePath } from '@constant/iyagigo/route-path';
import { GUEST_VOCABULARY_CLOZE_QUOTA, GUEST_GRAMMAR_CLOZE_QUOTA } from '@constant/iyagigo/variable';
import { useMySubscriptionQuery } from '@hook/billing/useApi';
import { EApiSubscriptionStatus } from '@datatype/billing/api-type';
import { EApiLanguageCode } from '@datatype/iyagigo/api-type';
import IconTextBlock from '@component/common/block/icon-text-block';
import ImageBlock from '@component/common/block/image-block';
import otterTestImage from '@asset/image/otter-test.webp';
import vocaCardImage from '@asset/image/voca-card.webp';
import incorrectPageImage from '@asset/image/incorrect-page.webp';
import Spacer from '@component/common/spacer';
import Skeleton from '@design-system/skeleton';
import TextButton from '@design-system/text-button';
import Canvas from '@design-system/canvas';
import { ETheme } from '@datatype/type';
import Scroll, { ScrollContainer } from '@component/common/scroll';
import { getAttendanceType } from '@util/attendance';
import { IS_DEMO, IS_LISTENING_RELEASE } from '@constant/variable';

const CLOZE_SUB_MENU_IDS = IS_LISTENING_RELEASE
  ? (['vocabulary', 'grammar', 'listening'] as const)
  : (['vocabulary', 'grammar'] as const);

// ############### variable ###############
// const BUTTON_WIDTH = IS_IYAGIGO_MOBILE_VIEW ? '100%' : '200px';
const BUTTON_WIDTH = '100%';
const TODAY_STATUS_WIDTH = '180px';

const ReviewButtonContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  gap: ${size.MARGIN_1};
  width: 100%;
`;

const ReviewCardInner = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
  gap: ${size.MARGIN_1};
  width: 100%;
`;

const TodayStatusContainer = styled.div`
  position: relative;
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${size.MARGIN_2};
  width: ${TODAY_STATUS_WIDTH};
`;

const BannerContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: ${size.MARGIN_2};
  width: 100%;
`;

const BannerContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const flicker = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1) rotate(-1deg);
  }
  20% {
    opacity: 0.85;
    transform: scale(1.05) rotate(1deg);
  }
  40% {
    opacity: 1;
    transform: scale(0.98) rotate(-2deg);
  }
  60% {
    opacity: 0.9;
    transform: scale(1.03) rotate(2deg);
  }
  80% {
    opacity: 1;
    transform: scale(1) rotate(-1deg);
  }
`;

const FireContainer = styled.div`
  width: 100%;

  & svg {
    transform-origin: center bottom;
    animation: ${flicker} 1.2s infinite ease-in-out;
  }
`;

const TEST_DESCRIPTION_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: false },
  box: { alignX: 'left' },
} as const;

const REVIEW_BUTTON_IMAGE_DESIGN = { image: { height: '100%' }, box: { height: '35px', width: '50px' } } as const;
const REVIEW_BUTTON_TEXT_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: false },
} as const;

const ClozeHome = () => {
  // ############### state ###############
  const dispatch = useTypedDispatch();
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;
  const todaySubMenu = useCurrentSubMenu(CLOZE_SUB_MENU_IDS, DEFAULT_IYAGIGO_SUB_MENU.cloze as 'vocabulary');
  const currentEnglishVocabularyLevel =
    useTypedSelector((state) => state.rootReducer?.studentReducer?.currentEnglishVocabularyLevel) ?? null;
  const currentEnglishGrammarLevel =
    useTypedSelector((state) => state.rootReducer?.studentReducer?.currentEnglishGrammarLevel) ?? null;
  const currentJapaneseVocabularyLevel =
    useTypedSelector((state) => state.rootReducer?.studentReducer?.currentJapaneseVocabularyLevel) ?? null;
  const currentJapaneseGrammarLevel =
    useTypedSelector((state) => state.rootReducer?.studentReducer?.currentJapaneseGrammarLevel) ?? null;
  const currentVocabularyLevel =
    studyLanguage === EApiLanguageCode.ENGLISH ? currentEnglishVocabularyLevel : currentJapaneseVocabularyLevel;
  const currentGrammarLevel =
    studyLanguage === EApiLanguageCode.ENGLISH ? currentEnglishGrammarLevel : currentJapaneseGrammarLevel;
  const isListening = todaySubMenu === 'listening';

  // ############### hook ###############
  const navigate = useNavigate();
  const { openAuthPage } = useAuthPage();
  const playSound = usePlaySound();

  // ############### api ###############
  const workbookQuery = useTodayClozeWorkbookQuery(
    getClozeTypeBySubject(todaySubMenu),
    studyLanguage === EApiLanguageCode.ENGLISH ? EApiLanguageCode.ENGLISH : EApiLanguageCode.JAPANESE,
  );
  const subscriptionQuery = useMySubscriptionQuery();
  const isSubscriber = subscriptionQuery.data?.data?.status === EApiSubscriptionStatus.ACTIVE;
  const studentQuery = useStudentKeyQuery();
  const studyConfiguration = studentQuery.data?.data?.studyConfiguration;

  // ############### Sync & Calculate Progress ###############
  const workbookProgress = useWorkbookSync(workbookQuery.data?.data || []);
  const { studiedWorkbookCount, totalWorkbookCount, isProgress, isComplete } = workbookProgress;

  // studyConfiguration에서 일일목표 읽기
  const dailyGoal = getClozeGoal(studyConfiguration, studyLanguage, todaySubMenu);

  const TODAY_TAB: { id: string; name: string }[] = [
    { id: 'vocabulary', name: '어휘' },
    { id: 'grammar', name: '문법' },
    ...(IS_LISTENING_RELEASE ? [{ id: 'listening', name: '듣기' }] : []),
  ];
  const studyLanguageText = IS_DEMO ? '' : `${studyLanguage === EApiLanguageCode.ENGLISH ? '영어' : '일본어'} `;

  let StudyDescription;

  switch (todaySubMenu) {
    case 'vocabulary':
      StudyDescription = (
        <TextBlock
          text={`매일 <strong style="color: #A26939;">하루 ${dailyGoal}개</strong>의 <strong style="color: #A26939;">빈칸채우기</strong>로 ${studyLanguageText}실력을 키워요`}
          design={TEST_DESCRIPTION_DESIGN}
        />
      );
      break;
    case 'grammar':
      StudyDescription = (
        <TextBlock
          text={`매일 <strong style="color: #A26939;">하루 ${dailyGoal}개</strong>의 <strong style="color: #A26939;">문장만들기</strong>로 ${studyLanguageText}실력을 키워요`}
          design={TEST_DESCRIPTION_DESIGN}
        />
      );
      break;
    case 'listening':
      StudyDescription = (
        <TextBlock
          text={`매일 <strong style="color: #A26939;">하루 ${dailyGoal}개</strong>의 <strong style="color: #A26939;">듣기 문제</strong>로 ${studyLanguageText}듣기 실력을 키워요`}
          design={TEST_DESCRIPTION_DESIGN}
        />
      );
      break;
  }

  // ############### function ###############
  const getLevelName = (level: TClozeLevel) => {
    switch (level) {
      case EApiClozeLevel.EASY:
        return '쉬움';
      case EApiClozeLevel.NORMAL:
        return '보통';
      case EApiClozeLevel.HARD:
        return '어려움';
      default:
        return '';
      // throw new Error('invalid level');
    }
  };

  // ############### handler ###############
  const handleTabClick = (clozeSubMenu: TIyagigoSubMenu['cloze']) => {
    // playSound('tap');
    dispatch(navigationActions.storeIyagigoSubMenu({ mainMenu: 'cloze', subMenu: clozeSubMenu }));
  };

  const handleStudyButtonClick = () => {
    // playSound('tap');

    // 임시 테스트
    if (isComplete) {
      if (!isAuthenticated) {
        dispatch(
          modalActions.showDialogModal({
            type: 'need-signin',
            data: '로그인하면 문제를 다시 볼 수 있어요',
          }),
        );
        return;
      }
      dispatch(workbookActions.configureReviewWorkbook());
      navigate(createClozePath('study'));
      return;
    }

    if (isProgress) {
      // 한도 도달 Wall (quota) — 이어하기 시 한도 초과면 진입 차단
      // 비회원/가입자 한도 동일 → 구독 Wall로 통일 (가입해도 한도 동일하므로)
      const quota = todaySubMenu === 'vocabulary' ? GUEST_VOCABULARY_CLOZE_QUOTA : GUEST_GRAMMAR_CLOZE_QUOTA;
      if (!isSubscriber && studiedWorkbookCount >= quota) {
        dispatch(
          modalActions.showWallToast({
            type: 'subscription-wall',
            data: { trigger: 'quota', feature: 'cloze', quota, attendanceType: getAttendanceType(studyLanguage, todaySubMenu) },
          }),
        );
        return;
      }
      dispatch(workbookActions.configureTodayWorkbook());
      navigate(createClozePath('study'));
      return;
    }

    dispatch(workbookActions.configureTodayWorkbook());
    dispatch(modalActions.showDialogModal({ type: 'today-study', data: { study: todaySubMenu } }));
  };

  const handleClozeNoteClick = (type: 'bookmark' | 'incorrect') => {
    if (!isAuthenticated) {
      // playSound('ban');
      dispatch(
        modalActions.showWallToast({
          type: 'signup-wall',
          data: { trigger: 'access' },
        }),
      );
      return;
    }
    // playSound('tap');
    switch (type) {
      case 'bookmark':
        dispatch(workbookActions.configureBookmarkWorkbook({}));
        break;
      case 'incorrect':
        dispatch(workbookActions.configureIncorrectWorkbook({}));
        break;
    }
    navigate(createClozePath('note'));
  };

  const handleGoSigninClick = () => {
    dispatch(modalActions.hideOverlay());
    openAuthPage('signin');
  };

  const handleLevelSelect = () => {
    // TODO(listening-api): 듣기 난이도 설정 필드와 수정 API 연결 후 설정 모달을 연다.
    if (isListening) {
      dispatch(
        modalActions.showDialogModal({
          type: 'plain-text',
          data: '듣기 난이도 설정은 준비 중이에요',
        }),
      );
      return;
    }
    dispatch(
      modalActions.showDialogModal({
        type: 'study-configuration',
        data: {
          mode:
            studyLanguage === EApiLanguageCode.ENGLISH
              ? todaySubMenu === 'vocabulary'
                ? EStudyConfigurationMode.STUDY_ENGLISH_VOCABULARY_LEVEL
                : EStudyConfigurationMode.STUDY_ENGLISH_GRAMMAR_LEVEL
              : todaySubMenu === 'vocabulary'
                ? EStudyConfigurationMode.STUDY_JAPANESE_VOCABULARY_LEVEL
                : EStudyConfigurationMode.STUDY_JAPANESE_GRAMMAR_LEVEL,
        },
      }),
    );
  };

  return (
    <WorkbookTemplate
      tabs={TODAY_TAB}
      selectedTabId={todaySubMenu ?? undefined}
      onTabClick={(id) => handleTabClick(id as TIyagigoSubMenu['cloze'])}>
      {/*<TextBlock*/}
      {/*  text={`오늘의 ${todaySubMenu === 'vocabulary' ? '어휘' : '문법'} 학습`}*/}
      {/*  design={{*/}
      {/*    text: { size: 'medium', color: color.BLACK_2, bold: true },*/}
      {/*    box: { alignX: 'left' },*/}
      {/*  }}*/}
      {/*/>*/}
      {/*<Gap height={size.MARGIN_1} />*/}
      <BannerContainer>
        <ImageBlock src={otterTestImage} alt='otter' design={{ image: { height: '80px' } }} />
        <BannerContent>
          <TextBlock text='현재 난이도' design={{ text: { size: 'medium', color: color.BLACK_2 } }} />
          {studentQuery.isLoading ? (
            <Skeleton />
          ) : (
            <TextBlock
              text={
                // TODO(listening-api): 듣기 난이도 필드가 연결되면 하드코딩된 '보통' 대신 실제 설정값을 표시한다.
                isListening
                  ? '보통'
                  : getLevelName(todaySubMenu === 'vocabulary' ? currentVocabularyLevel : currentGrammarLevel)
              }
              design={{
                text: { size: 'large', color: color.BLACK_2, bold: true },
              }}
            />
          )}
          <IconTextBlock
            icon={'edit'}
            text='난이도 조절하기'
            arrangement='text-icon'
            design={{
              text: { size: 'tiny', color: color.GRAY_1, underline: false },
              icon: { size: 'tiny', color: color.GRAY_1, kind: 'outlined' },
              box: { event: { cursor: 'pointer' }, gap: '2px' },
            }}
            click={{ handler: handleLevelSelect }}
          />
        </BannerContent>
      </BannerContainer>
      <Gap height={size.MARGIN_1} />
      {studentQuery.isLoading ? (
        <Skeleton />
      ) : (
        <Canvas theme={ETheme.BROWN} shape={'square'} width='100%'>
          {StudyDescription}
        </Canvas>
      )}
      <Gap height={size.MARGIN_2} />
      <TodayStatusContainer>
        {studentQuery.isLoading ? (
          <Skeleton width='100%' height='80px' />
        ) : (
          <>
            {/* TODO(listening-api): 듣기 goal 수정 API가 연결되면 임시 TextBlock을 제거하고 듣기 전용
            EStudyConfigurationMode를 사용하는 AssignmentControl로 교체한다. */}
            {isListening ? (
              <TextBlock
                text={`오늘 ${dailyGoal}개`}
                design={{ text: { size: 'medium', color: color.BLACK_2, bold: true } }}
              />
            ) : (
              <AssignmentControl
                goal={dailyGoal}
                type={
                  studyLanguage === EApiLanguageCode.ENGLISH
                    ? todaySubMenu === 'vocabulary'
                      ? EStudyConfigurationMode.STUDY_ENGLISH_VOCABULARY_COUNT
                      : EStudyConfigurationMode.STUDY_ENGLISH_GRAMMAR_COUNT
                    : todaySubMenu === 'vocabulary'
                      ? EStudyConfigurationMode.STUDY_JAPANESE_VOCABULARY_COUNT
                      : EStudyConfigurationMode.STUDY_JAPANESE_GRAMMAR_COUNT
                }
                suffix={'개'}
              />
            )}
            <PieProgress studiedCount={studiedWorkbookCount} totalCount={totalWorkbookCount} />
          </>
        )}
      </TodayStatusContainer>
      {/*</Blur>*/}
      <Gap height={size.MARGIN_1} />
      <TextButton
        text={isComplete ? '다시보기' : isProgress ? '이어하기' : '학습하기'}
        theme={ETheme.BROWN}
        size={'medium'}
        shape='pill'
        width={BUTTON_WIDTH}
        // disabled={isComplete}
        handleClick={handleStudyButtonClick}
        isLoading={studentQuery.isLoading}
      />
      <Gap height={size.MARGIN_2} />
      <Spacer />
      <ReviewButtonContainer>
        <Canvas
          theme={ETheme.BLUE}
          shadow={true}
          shape={'round'}
          width='100%'
          handleClick={() => handleClozeNoteClick('bookmark')}>
          <ReviewCardInner>
            <ImageBlock src={vocaCardImage} alt='반복 학습' design={REVIEW_BUTTON_IMAGE_DESIGN} />
            <TextBlock text='저장한 문제 보기' design={REVIEW_BUTTON_TEXT_DESIGN} />
          </ReviewCardInner>
        </Canvas>
        <Canvas
          theme={ETheme.RED}
          shadow={true}
          shape={'round'}
          width='100%'
          handleClick={() => handleClozeNoteClick('incorrect')}>
          <ReviewCardInner>
            <ImageBlock src={incorrectPageImage} alt='틀린 문제 학습' design={REVIEW_BUTTON_IMAGE_DESIGN} />
            <TextBlock text='틀린 문제 보기' design={REVIEW_BUTTON_TEXT_DESIGN} />
          </ReviewCardInner>
        </Canvas>
      </ReviewButtonContainer>
    </WorkbookTemplate>
  );
};

export default ClozeHome;
